import React, { useRef, useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { validateFunc } from '../../constraints/constraints'
import { withTranslation } from 'react-i18next'
import { editCoupon, createCoupon, getCoupons } from '../../apollo'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import {
  Box,
  Switch,
  Typography,
  Input,
  Button,
  Alert,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import FormCardHeader from '../FormCardHeader'

const CREATE_COUPON = gql`
  ${createCoupon}
`
const EDIT_COUPON = gql`
  ${editCoupon}
`
const GET_COUPONS = gql`
  ${getCoupons}
`

function Category(props) {
  const formRef = useRef()
  const title = props.coupon ? props.coupon.title : ''
  const discount = props.coupon ? props.coupon.discount : ''
  const maxCouponLimit = props.coupon ? props.coupon.maxCouponLimit : ''
  const customerUsageLimit = props.coupon ? props.coupon.customerUsageLimit : ''
  const [enabled, setEnabled] = useState(
    props.coupon ? props.coupon.enabled : false
  )
  const [discountType, setDiscountType] = useState(
    props.coupon ? props.coupon.discountType : 'PERCENTAGE'
  )
  // Free delivery vouchers share the coupon model but only ever take a
  // percentage off the delivery fee (100 = free delivery)
  const isDeliveryVoucher =
    props.deliveryVoucher || props.coupon?.appliesTo === 'DELIVERY'
  const mutation = props.coupon ? EDIT_COUPON : CREATE_COUPON
  const [mainError, mainErrorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [titleError, titleErrorSetter] = useState(null)
  const [discountError, discountErrorSetter] = useState(null)
  const onBlur = (setter, field, state) => {
    setter(!validateFunc({ [field]: state }, field))
  }
  // Delivery vouchers and percentages are capped at 100%; a fixed amount
  // off (in £) can be any positive value.
  const isDiscountValid = value => {
    const amount = Number(value)
    if (value === '' || Number.isNaN(amount) || amount <= 0) return false
    return isDeliveryVoucher || discountType !== 'FIXED' ? amount <= 100 : true
  }
  const onCompleted = data => {
    const message = props.coupon ? t('CouponUpdated') : t('CouponAdded')
    successSetter(message)
    mainErrorSetter('')
    if (!props.coupon) clearFields()
  }
  const onError = error => {
    let message = ''
    try {
      message = error.graphQLErrors[0].message
    } catch (err) {
      message = t('ActionFailedTryAgain')
    }
    successSetter('')
    mainErrorSetter(message)
  }
  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_COUPONS }],
    onError,
    onCompleted
  })

  const onSubmitValidaiton = () => {
    const titleError = !validateFunc(
      { title: formRef.current['input-code'].value },
      'title'
    )
    const discountValue = formRef.current['input-discount'].value
    const discountError = isDiscountValid(discountValue)
    titleErrorSetter(titleError)
    discountErrorSetter(discountError)
    return titleError && discountError
  }
  const clearFields = () => {
    formRef.current.reset()
    titleErrorSetter(null)
    discountErrorSetter(null)
  }

  const { t } = props
  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  const parseNullableNumber = value => {
    if (value === '' || value === null || typeof value === 'undefined') {
      return null
    }
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }

  return (
    <Box container className={classes.container}>
      <FormCardHeader
        className={classes.heading}
        title={
          isDeliveryVoucher
            ? props.coupon
              ? t('EditFreeDeliveryVoucher')
              : t('AddFreeDeliveryVoucher')
            : props.coupon
            ? t('EditCoupon')
            : t('AddCoupon')
        }
        onClose={props.onClose}
        action={
          <Box className={globalClasses.flexRow} sx={{ gap: 0.5 }}>
            <label>{enabled ? t('Disable') : t('Enable')}</label>
            <Switch
              defaultChecked={enabled}
              value={enabled}
              onChange={e => setEnabled(e.target.checked)}
              id="input-enabled"
              name="input-enabled"
              style={{ color: 'black' }}
            />
          </Box>
        }
      />
      <Box className={classes.form}>
        <form ref={formRef}>
          <Box className={globalClasses.flexRow}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <Typography className={classes.labelText}>
                    {t('Code')}
                  </Typography>
                  <Tooltip title={t('Universal unique code for the coupon')}>
                    <InfoIcon
                      sx={{
                        ml: 0.5,
                        fontSize: '0.9rem',
                        color: 'grey.500',
                        cursor: 'pointer'
                      }}
                    />
                  </Tooltip>
                </Box>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-code"
                  name="input-code"
                  placeholder={t('PHCode')}
                  type="text"
                  defaultValue={title}
                  onBlur={event =>
                    onBlur(titleErrorSetter, 'title', event.target.value)
                  }
                  disableUnderline
                  className={[
                    globalClasses.input,
                    titleError === false
                      ? globalClasses.inputError
                      : titleError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Grid>
              {!isDeliveryVoucher && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center">
                    <Typography className={classes.labelText}>
                      {t('Discount Type')}
                    </Typography>
                    <Tooltip
                      title={t(
                        'Select if the discount is a percentage or a fixed amount'
                      )}>
                      <InfoIcon
                        sx={{
                          ml: 0.5,
                          fontSize: '0.9rem',
                          color: 'grey.500',
                          cursor: 'pointer'
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <Select
                    id="input-discount-type"
                    name="input-discount-type"
                    value={discountType}
                    onChange={e => setDiscountType(e.target.value)}
                    displayEmpty
                    className={globalClasses.input}
                    style={{ marginTop: -1 }}>
                    <MenuItem value="PERCENTAGE" style={{ color: '#000000' }}>
                      Percentage
                    </MenuItem>
                    <MenuItem value="FIXED" style={{ color: '#000000' }}>
                      Fixed
                    </MenuItem>
                  </Select>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                  <Typography className={classes.labelText}>
                    {isDeliveryVoucher
                      ? t('DeliveryFeeDiscountPercent')
                      : t('Discount')}
                  </Typography>
                  <Tooltip
                    title={
                      isDeliveryVoucher
                        ? t('DeliveryFeeDiscountHelp')
                        : t('The value of the discount')
                    }>
                    <InfoIcon
                      sx={{
                        ml: 0.5,
                        fontSize: '0.9rem',
                        color: 'grey.500',
                        cursor: 'pointer'
                      }}
                    />
                  </Tooltip>
                </Box>
                <Input
                  style={{ marginTop: -1, width: '95%' }}
                  id="input-discount"
                  name="input-discount"
                  placeholder={
                    isDeliveryVoucher
                      ? '100'
                      : discountType === 'FIXED'
                      ? t('PHDiscountFixed')
                      : t('PHDiscount')
                  }
                  type="number"
                  inputProps={
                    isDeliveryVoucher || discountType !== 'FIXED'
                      ? { min: 1, max: 100 }
                      : { min: 0.01, step: 0.01 }
                  }
                  defaultValue={discount}
                  onBlur={event => {
                    discountErrorSetter(isDiscountValid(event.target.value))
                  }}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    discountError === false
                      ? globalClasses.inputError
                      : discountError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <Typography className={classes.labelText}>
                    Max Coupon Limit
                  </Typography>
                  <Tooltip title="Total maximum times this coupon can be used">
                    <InfoIcon
                      sx={{
                        ml: 0.5,
                        fontSize: '0.9rem',
                        color: 'grey.500',
                        cursor: 'pointer'
                      }}
                    />
                  </Tooltip>
                </Box>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-max-coupon-limit"
                  name="input-max-coupon-limit"
                  placeholder="e.g. 100"
                  type="number"
                  defaultValue={maxCouponLimit}
                  disableUnderline
                  className={globalClasses.input}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <Typography className={classes.labelText}>
                    Customer Usage Limit
                  </Typography>
                  <Tooltip title="How many times one customer can use this coupon">
                    <InfoIcon
                      sx={{
                        ml: 0.5,
                        fontSize: '0.9rem',
                        color: 'grey.500',
                        cursor: 'pointer'
                      }}
                    />
                  </Tooltip>
                </Box>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-customer-usage-limit"
                  name="input-customer-usage-limit"
                  placeholder="e.g. 1"
                  type="number"
                  defaultValue={customerUsageLimit}
                  disableUnderline
                  className={globalClasses.input}
                />
              </Grid>
            </Grid>
          </Box>

          {loading ? t('Loading') : null}
          <Box>
            <Button
              className={globalClasses.button}
              disabled={loading}
              onClick={async e => {
                e.preventDefault()
                if (onSubmitValidaiton() && !loading) {
                  mutate({
                    variables: {
                      couponInput: {
                        _id: props.coupon ? props.coupon._id : '',
                        title: formRef.current['input-code'].value,
                        discount: +formRef.current['input-discount'].value,
                        enabled: enabled,
                        discountType: isDeliveryVoucher
                          ? 'PERCENTAGE'
                          : discountType,
                        appliesTo: isDeliveryVoucher ? 'DELIVERY' : 'ORDER',
                        maxCouponLimit: parseNullableNumber(
                          formRef.current['input-max-coupon-limit']?.value
                        ),
                        customerUsageLimit: parseNullableNumber(
                          formRef.current['input-customer-usage-limit']?.value
                        )
                      }
                    }
                  })
                }
              }}>
              {t('Save')}
            </Button>
          </Box>
        </form>
        <Box mt={2}>
          {success && (
            <Alert
              className={globalClasses.alertSuccess}
              variant="filled"
              severity="success">
              {success}
            </Alert>
          )}
          {mainError && (
            <Alert
              className={globalClasses.alertError}
              variant="filled"
              severity="error">
              {mainError}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default withTranslation()(Category)
